
















































































































































import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from '@nuxtjs/composition-api';
import type { ComputedRef, PropType } from '@nuxtjs/composition-api';
import { SfModal } from '@storefront-ui/vue';
import { CONSENT_MANAGER_VENDOR, CONSENT_MANAGER_PURPOSE } from '~/constants/consentManager';
import { ProductImage } from '~/types/product/attribute/ProductImage/ProductImage';
import SimpleCarousel from '~/components/molecules/SimpleCarousel/SimpleCarousel.vue';
import { PRODUCT_GALLERY, ZOOM_ACTIONS, ZoomActions } from '~/constants/productGallery';
import { instanceOfProductImage } from '~/types/product/attribute/ProductImage/instanceOfProductImage';

export default defineComponent({
  components: { SfModal, SimpleCarousel },
  props: {
    galleryItems: {
      type: Array as PropType<ProductImage[]>,
      required: true,
      validator: (galleryItems: ProductImage[]) => {
        if (galleryItems.length === 0) {
          return false;
        }
        return galleryItems.every(galleryItem => instanceOfProductImage(galleryItem));
      },
    },
    activeIndex: {
      type: Number,
      required: true,
      validator: (activeIndex: number) => activeIndex >= 0,
    },
    galleryVideo: {
      type: Object,
      default: () => {
        return null;
      },
    },
  },
  // TODO - refs is deprecated! to be removed on Vue 3
  setup(props, { emit, refs }) {
    const zoomLevel = ref<number>(0);
    const maxZoomLevel: number = 2;
    const minZoomLevel: number = 0;
    const minIndex = 0;
    const maxIndex: ComputedRef<number> = computed(() => galleryItemsIncludingVideo.value.length - 1);
    const maxImgPerView = ref<number>(4);

    const isZoomOutDisabled: ComputedRef<boolean> = computed(() => zoomLevel.value <= minZoomLevel);
    const isZoomInDisabled: ComputedRef<boolean> = computed(() => zoomLevel.value >= maxZoomLevel);
    const isModalShown: ComputedRef<boolean> = computed(() => props.activeIndex <= props.galleryItems.length);
    const isPreviewsShown: ComputedRef<boolean> = computed(() => props.galleryItems.length > 1);
    const imgPosition: { x: number, y: number } = { x: null, y: null };
    const galleryPreviews = ref<HTMLElement | null>(null);
    const previewImgContainer = ref<HTMLElement | null>(null);
    const galleryItemsIncludingVideo = computed(() =>
      props.galleryVideo ? [...props.galleryItems, props.galleryVideo] : props.galleryItems,
    );
    const disabledClass = ref('opacity-70');
    const isVideoShown = computed(() =>
      props.galleryVideo && props.activeIndex === galleryItemsIncludingVideo.value.length - 1,
    );
    const isMaxVisibleImgsShowed = computed(() => galleryItemsIncludingVideo.value.length > maxImgPerView.value);

    const scaleImg = computed(() => {
      const defaultScale = 1;
      return `scale(${defaultScale + zoomLevel.value})`;
    });

    const iframeVideoLink = computed(() => {
      if (!isVideoShown.value) {
        return '';
      }
      const shouldAutoplay = false;
      return `${props.galleryVideo.play.url}?autoplay=${shouldAutoplay ? '1' : '0'}`;
    });

    const changeZoomLevel = (action: ZoomActions, value?: number) => {
      if (!activeCarouselRef()) {
        return;
      }
      const limitValue = {
        [ZOOM_ACTIONS.INCREASE]: maxZoomLevel,
        [ZOOM_ACTIONS.DECREASE]: minZoomLevel,
      };
      const isLimitValue = {
        [ZOOM_ACTIONS.INCREASE]: zoomLevel.value + value > maxZoomLevel,
        [ZOOM_ACTIONS.DECREASE]: zoomLevel.value + value < minZoomLevel,
      };

      const newZoomLevel = isLimitValue[action] ? limitValue[action] : zoomLevel.value + value;
      zoomLevel.value = action === ZOOM_ACTIONS.RESET ? minZoomLevel : newZoomLevel;
      activeCarouselRef().style.transform = scaleImg.value;
    };

    const onWheel = (event: WheelEvent) => {
      const wheelZoom = 0.25;
      if (event.deltaY < 0) {
        changeZoomLevel(ZOOM_ACTIONS.INCREASE, wheelZoom);
      } else {
        changeZoomLevel(ZOOM_ACTIONS.DECREASE, -wheelZoom);
      }
    };

    const onMouseMove = (event) => {
      if (zoomLevel.value <= minZoomLevel) {
        return;
      }
      activeCarouselRef().style.transformOrigin = `${
        event.clientX - imgPosition.x
      }px ${event.clientY - imgPosition.y}px`;
    };

    const onArrow = (arrow) => {
      const prevLeft = galleryPreviews.value.scrollLeft;
      const width = previewImgContainer.value[0].getBoundingClientRect().width;
      // on left side
      if (arrow === 'left') {
        galleryPreviews.value.scrollLeft = prevLeft - width;
        // on right side
      } else {
        galleryPreviews.value.scrollLeft = prevLeft + width;
      }
    };

    const onActiveImgChange = (index) => {
      if (index < minIndex || index > maxIndex) {
        return;
      }
      emit('change', index % galleryItemsIncludingVideo.value.length);
    };

    const closeModal = () => {
      emit('close');
    };

    const activeCarouselRef = () => {
      return refs[`carouselItem${props.activeIndex}`] as HTMLElement;
    };

    onMounted(() => {
      // need to hide the overflow from the html in order not to conflict with the onWheel
      document.querySelector('html').style.overflowY = 'hidden';
      if (activeCarouselRef()) {
        imgPosition.x = activeCarouselRef().getBoundingClientRect().x;
        imgPosition.y = activeCarouselRef().getBoundingClientRect().y;
      }
      onActiveImgChange(props.activeIndex);
    });

    onBeforeUnmount(() => {
      // set back html overflow from hidden(inside the onMounted) to unset(not 'auto' because it breaks sticky elements)
      document.querySelector('html').style.overflowY = 'unset';
    });

    return {
      galleryItemsIncludingVideo,
      isModalShown,
      isVideoShown,
      isPreviewsShown,
      changeZoomLevel,
      ZOOM_ACTIONS,
      onMouseMove,
      isZoomInDisabled,
      isZoomOutDisabled,
      zoomLevel,
      onWheel,
      disabledClass,
      onArrow,
      galleryPreviews,
      PRODUCT_GALLERY,
      CONSENT_MANAGER_VENDOR,
      CONSENT_MANAGER_PURPOSE,
      previewImgContainer,
      maxImgPerView,
      onActiveImgChange,
      isMaxVisibleImgsShowed,
      minIndex,
      maxIndex,
      closeModal,
      iframeVideoLink,
      activeCarouselRef,
    };
  },
});

